import React, { useEffect, useState } from 'react';
import { X, FileText, ChevronDown, ChevronRight, ExternalLink, Sparkles, RefreshCw } from 'lucide-react';
import { knowledgeService } from '../services/knowledgeService';

type EntityPanelNode = {
  id: string;
  label: string;
  entityType?: string;
  properties?: Record<string, string>;
};

type EntityPanelEdge = {
  source: string | EntityPanelNode;
  target: string | EntityPanelNode;
  label: string;
  context?: string;
};

type SourceDoc = {
  docId: string;
  docName: string;
  category: string;
  chunks: Array<{ chunkIndex: number; pageNumber?: number; excerpt: string }>;
};

type EnrichSuggestion = {
  from: string;
  to: string;
  type: string;
  context: string;
  selected: boolean;
};

type EntityPanelProps = {
  node: EntityPanelNode | null;
  edges: EntityPanelEdge[];
  onClose: () => void;
  onNavigateToEntity: (entityName: string) => void;
  onViewInChat: (entityName: string) => void;
  onGraphRefresh?: () => void;
  orgId?: string;
  userId?: string;
};

const edgeId = (edge: EntityPanelEdge): string => {
  const src = typeof edge.source === 'object' ? edge.source.id : edge.source;
  const tgt = typeof edge.target === 'object' ? edge.target.id : edge.target;
  return `${src}::${edge.label}::${tgt}`;
};

export const EntityPanel: React.FC<EntityPanelProps> = ({
  node,
  edges,
  onClose,
  onNavigateToEntity,
  onViewInChat,
  onGraphRefresh,
  orgId,
  userId,
}) => {
  const [sources, setSources] = useState<SourceDoc[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  // Enrich panel
  const [enrichPanelOpen, setEnrichPanelOpen] = useState(false);
  const [aiComment, setAiComment] = useState('');
  const [markedForDeletion, setMarkedForDeletion] = useState<Set<string>>(new Set()); // edge ids
  const [enrichSuggestions, setEnrichSuggestions] = useState<EnrichSuggestion[] | null>(null);
  const [isFindingWithAI, setIsFindingWithAI] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [enrichResult, setEnrichResult] = useState<{ added: number; deleted: number } | null>(null);

  useEffect(() => {
    if (!node) { setSources([]); return; }
    setLoadingSources(true);
    setSources([]);
    setExpandedDocs(new Set());
    setEnrichPanelOpen(false);
    setAiComment('');
    setMarkedForDeletion(new Set());
    setEnrichSuggestions(null);
    setEnrichResult(null);
    knowledgeService.getEntityDetails(node.label, orgId, userId)
      .then(res => setSources(res.sources || []))
      .catch(() => setSources([]))
      .finally(() => setLoadingSources(false));
  }, [node?.label, orgId, userId]);

  if (!node) return null;

  const outgoing = edges.filter(e => {
    const src = typeof e.source === 'object' ? e.source.id : e.source;
    return src === node.id;
  });
  const incoming = edges.filter(e => {
    const tgt = typeof e.target === 'object' ? e.target.id : e.target;
    return tgt === node.id;
  });

  const allEdges = [...outgoing, ...incoming];

  const toggleDoc = (docId: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      next.has(docId) ? next.delete(docId) : next.add(docId);
      return next;
    });
  };

  const handleFindWithAI = async () => {
    if (!node) return;
    setIsFindingWithAI(true);
    setEnrichSuggestions(null);
    try {
      const result = await knowledgeService.enrichGraphPreview(undefined, orgId, userId, node.label, aiComment || undefined);
      setEnrichSuggestions((result.suggestions || []).map(s => ({ ...s, selected: true })));
    } catch {
      setEnrichSuggestions([]);
    } finally {
      setIsFindingWithAI(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!node) return;
    setIsApplying(true);

    const toDelete = allEdges
      .filter(e => markedForDeletion.has(edgeId(e)))
      .map(e => ({
        from: typeof e.source === 'object' ? e.source.id : e.source,
        to: typeof e.target === 'object' ? e.target.id : e.target,
        type: e.label,
      }));

    const toAdd = (enrichSuggestions || [])
      .filter(s => s.selected)
      .map(s => ({ from: s.from, to: s.to, type: s.type }));

    try {
      const [deleteResult, addResult] = await Promise.all([
        toDelete.length > 0
          ? knowledgeService.deleteGraphRelationships(toDelete, orgId, userId)
          : Promise.resolve({ success: true, deleted: 0 }),
        toAdd.length > 0
          ? knowledgeService.enrichGraphApply(toAdd, orgId, userId)
          : Promise.resolve({ success: true, newRelationships: 0 }),
      ]);
      setEnrichResult({
        deleted: deleteResult.deleted ?? 0,
        added: (addResult as { newRelationships?: number }).newRelationships ?? 0,
      });
      setEnrichPanelOpen(false);
      setMarkedForDeletion(new Set());
      setEnrichSuggestions(null);
      setAiComment('');
      onGraphRefresh?.();
    } catch {
      setEnrichResult({ deleted: -1, added: -1 });
    } finally {
      setIsApplying(false);
    }
  };

  const selectedToAdd = (enrichSuggestions || []).filter(s => s.selected).length;
  const selectedToDelete = markedForDeletion.size;

  return (
    <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto flex-shrink-0">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{node.label}</h3>
            <span className="inline-block mt-0.5 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
              {node.entityType || 'Entity'}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-5">

          {/* Source Documents */}
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Source Documents {sources.length > 0 && `(${sources.length})`}
            </span>
            {loadingSources && <p className="text-xs text-gray-400 mt-1 italic">Loading sources…</p>}
            {!loadingSources && sources.length === 0 && (
              <p className="text-xs text-gray-400 mt-1 italic">No source documents found</p>
            )}
            {!loadingSources && sources.length > 0 && (
              <div className="mt-2 space-y-2">
                {sources.map(src => (
                  <div key={src.docId} className="border border-gray-100 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleDoc(src.docId)}
                      className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="flex-1 text-xs font-medium text-gray-800 truncate">{src.docName}</span>
                      {src.category && <span className="text-xs text-gray-400 flex-shrink-0">{src.category}</span>}
                      {expandedDocs.has(src.docId)
                        ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      }
                    </button>
                    {expandedDocs.has(src.docId) && (
                      <div className="divide-y divide-gray-50">
                        {src.chunks.map((chunk, i) => (
                          <div key={i} className="px-3 py-2">
                            <span className="text-xs text-gray-400">
                              Chunk {chunk.chunkIndex + 1}
                              {chunk.pageNumber != null && ` · Page ${chunk.pageNumber}`}
                            </span>
                            <p className="text-xs text-gray-600 leading-relaxed mt-1">…{chunk.excerpt}…</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Properties */}
          {node.properties && Object.keys(node.properties).length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Properties</span>
              <div className="mt-1 space-y-1">
                {Object.entries(node.properties).map(([key, value]) => (
                  <div key={key} className="text-xs">
                    <span className="text-gray-500">{key}:</span>{' '}
                    <span className="text-gray-800">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outgoing relationships */}
          {outgoing.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Outgoing ({outgoing.length})
              </span>
              <div className="mt-1 space-y-1">
                {outgoing.map((edge, i) => {
                  const targetId = typeof edge.target === 'object' ? edge.target.id : edge.target;
                  return (
                    <button
                      key={i}
                      onClick={() => onNavigateToEntity(targetId)}
                      className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100"
                    >
                      <div>
                        <span className="text-blue-600 font-medium">{edge.label}</span>
                        <span className="text-gray-400"> → </span>
                        <span className="text-gray-700">{targetId}</span>
                      </div>
                      {edge.context && <p className="text-gray-400 mt-0.5 truncate">{edge.context}</p>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Incoming relationships */}
          {incoming.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Incoming ({incoming.length})
              </span>
              <div className="mt-1 space-y-1">
                {incoming.map((edge, i) => {
                  const sourceId = typeof edge.source === 'object' ? edge.source.id : edge.source;
                  return (
                    <button
                      key={i}
                      onClick={() => onNavigateToEntity(sourceId)}
                      className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100"
                    >
                      <div>
                        <span className="text-gray-700">{sourceId}</span>
                        <span className="text-gray-400"> → </span>
                        <span className="text-blue-600 font-medium">{edge.label}</span>
                      </div>
                      {edge.context && <p className="text-gray-400 mt-0.5 truncate">{edge.context}</p>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Standalone notice */}
          {outgoing.length === 0 && incoming.length === 0 && (
            <div className="px-3 py-2 bg-yellow-50 border border-yellow-100 rounded-lg">
              <p className="text-xs text-yellow-700">This entity has no graph connections yet.</p>
            </div>
          )}

          {/* Enrich panel */}
          {enrichPanelOpen && (
            <div className="border border-purple-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-purple-50 flex items-center justify-between">
                <span className="text-xs font-semibold text-purple-800">Enrich Relationships</span>
                <button
                  onClick={() => {
                    setEnrichPanelOpen(false);
                    setMarkedForDeletion(new Set());
                    setEnrichSuggestions(null);
                    setAiComment('');
                  }}
                  className="text-purple-400 hover:text-purple-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="divide-y divide-gray-100">

                {/* Section 1: existing relationships — mark for removal */}
                {allEdges.length > 0 && (
                  <div className="px-3 py-2">
                    <p className="text-xs font-medium text-gray-500 mb-1.5">
                      Mark relationships to remove
                    </p>
                    <div className="space-y-1 max-h-36 overflow-y-auto">
                      {allEdges.map((edge, idx) => {
                        const isOut = idx < outgoing.length;
                        const peerId = isOut
                          ? (typeof edge.target === 'object' ? edge.target.id : edge.target)
                          : (typeof edge.source === 'object' ? edge.source.id : edge.source);
                        const eid = edgeId(edge);
                        const marked = markedForDeletion.has(eid);
                        return (
                          <div
                            key={idx}
                            className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${marked ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                          >
                            <span className={`flex-1 text-xs leading-tight ${marked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                              {isOut
                                ? <><span className="text-blue-600">{edge.label}</span> → {peerId}</>
                                : <>{peerId} → <span className="text-blue-600">{edge.label}</span></>
                              }
                            </span>
                            <button
                              onClick={() => {
                                setMarkedForDeletion(prev => {
                                  const next = new Set(prev);
                                  next.has(eid) ? next.delete(eid) : next.add(eid);
                                  return next;
                                });
                              }}
                              className={`flex-shrink-0 rounded p-0.5 transition-colors ${
                                marked
                                  ? 'text-red-500 hover:text-red-700 bg-red-100'
                                  : 'text-gray-300 hover:text-red-400 hover:bg-red-50'
                              }`}
                              title={marked ? 'Undo' : 'Mark for removal'}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Section 2: AI instruction + Find button */}
                <div className="px-3 py-2">
                  <label className="text-xs font-medium text-gray-500 block mb-1">
                    Instructions for AI (optional)
                  </label>
                  <textarea
                    value={aiComment}
                    onChange={e => setAiComment(e.target.value)}
                    placeholder='e.g. "link to return-to-play protocols"'
                    rows={2}
                    className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-purple-400 placeholder:text-gray-300"
                  />
                  <button
                    onClick={handleFindWithAI}
                    disabled={isFindingWithAI}
                    className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 transition-colors"
                  >
                    {isFindingWithAI
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <Sparkles className="w-3 h-3" />
                    }
                    {isFindingWithAI ? 'Finding…' : 'Find with AI'}
                  </button>
                </div>

                {/* Section 3: AI suggestions */}
                {enrichSuggestions !== null && (
                  <div className="px-3 py-2">
                    {enrichSuggestions.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No new relationships found</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-medium text-gray-500">
                            {enrichSuggestions.length} suggestion{enrichSuggestions.length !== 1 ? 's' : ''}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEnrichSuggestions(enrichSuggestions.map(s => ({ ...s, selected: true })))}
                              className="text-xs text-purple-600 hover:text-purple-800"
                            >All</button>
                            <button
                              onClick={() => setEnrichSuggestions(enrichSuggestions.map(s => ({ ...s, selected: false })))}
                              className="text-xs text-purple-600 hover:text-purple-800"
                            >None</button>
                          </div>
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {enrichSuggestions.map((s, idx) => (
                            <label key={idx} className="flex items-start gap-2 cursor-pointer hover:bg-purple-50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={s.selected}
                                onChange={e => {
                                  const updated = [...enrichSuggestions];
                                  updated[idx] = { ...s, selected: e.target.checked };
                                  setEnrichSuggestions(updated);
                                }}
                                className="mt-0.5 rounded border-purple-300 text-purple-600"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-purple-800 leading-tight">
                                  <span className="font-medium">{s.from}</span>
                                  <span className="text-purple-400 mx-1">→</span>
                                  <span className="font-medium text-purple-600">{s.type}</span>
                                  <span className="text-purple-400 mx-1">→</span>
                                  <span className="font-medium">{s.to}</span>
                                </div>
                                {s.context && <p className="text-xs text-gray-400 truncate mt-0.5">{s.context}</p>}
                              </div>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Apply footer */}
                <div className="px-3 py-2 bg-gray-50 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-400">
                    {selectedToDelete > 0 && `${selectedToDelete} to remove`}
                    {selectedToDelete > 0 && selectedToAdd > 0 && ' · '}
                    {selectedToAdd > 0 && `${selectedToAdd} to add`}
                  </span>
                  <button
                    onClick={handleApplyChanges}
                    disabled={isApplying || (selectedToDelete === 0 && selectedToAdd === 0)}
                    className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40 transition-colors"
                  >
                    {isApplying ? 'Applying…' : 'Apply Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Enrich result */}
          {enrichResult !== null && (
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-100 rounded-lg">
              <Sparkles className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
              <span className="text-xs text-purple-700 flex-1">
                {enrichResult.added === -1
                  ? 'Apply failed'
                  : [
                      enrichResult.added > 0 && `Added ${enrichResult.added}`,
                      enrichResult.deleted > 0 && `Removed ${enrichResult.deleted}`,
                    ].filter(Boolean).join(' · ') || 'No changes applied'}
              </span>
              <button onClick={() => setEnrichResult(null)} className="text-purple-400 hover:text-purple-600 text-xs">✕</button>
            </div>
          )}

          {/* Actions */}
          <div className="pt-2 border-t border-gray-100 flex gap-2">
            <button
              onClick={() => onViewInChat(node.label)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Ask Charlie
            </button>
            <button
              onClick={() => setEnrichPanelOpen(prev => !prev)}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors ${
                enrichPanelOpen
                  ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
              title="Enrich relationships for this entity"
            >
              <Sparkles className="w-3 h-3" />
              Enrich
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
